import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, Loader2, Mail, AlertTriangle, CheckCircle2, XCircle,
  Zap, Search, Clock, ShieldAlert, ShieldCheck, Users,
} from "lucide-react";

/**
 * AdminSubscriptions — platform-owner view of every company's subscription
 * state. Historical note: billing was one-time Stripe Checkout until the
 * 2026-08-09 migration to true recurring Stripe Subscriptions. "Legacy"
 * trial-expired rows are from pre-migration customers who never attached a
 * card to a real subscription — the Send Renewal flow emails them a
 * fresh Stripe checkout link that creates a proper recurring sub. New
 * customers signed up post-migration auto-renew on Stripe themselves.
 * This page surfaces stuck rows and gives admin one-click actions:
 *   • Send renewal link  — emails the owner a Stripe checkout URL for $97
 *   • Bulk expire        — runs the "flip expired trials → past_due + email" job
 *   • Cancel             — force-cancel a subscription
 */

const STATUS_STYLE = {
  trialing:      { label: "Trialing",      cls: "bg-amber-50 text-amber-700 border-amber-200",   icon: Clock },
  trial_expired: { label: "Trial expired", cls: "bg-rose-50 text-rose-700 border-rose-200",       icon: AlertTriangle },
  active:        { label: "Active",        cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: ShieldCheck },
  past_due:      { label: "Past due",      cls: "bg-rose-50 text-rose-700 border-rose-200",       icon: ShieldAlert },
  paused:        { label: "Paused",        cls: "bg-slate-100 text-slate-600 border-slate-200",    icon: Clock },
  free_claimed:  { label: "Free claimed",  cls: "bg-sky-50 text-sky-700 border-sky-200",           icon: CheckCircle2 },
  inactive:      { label: "Inactive",      cls: "bg-slate-100 text-slate-500 border-slate-200",    icon: XCircle },
  canceled:      { label: "Canceled",      cls: "bg-slate-100 text-slate-500 border-slate-200",    icon: XCircle },
  cancelled:     { label: "Canceled",      cls: "bg-slate-100 text-slate-500 border-slate-200",    icon: XCircle },
};

function StatusBadge({ status, trialExpired }) {
  const key = trialExpired ? "trial_expired" : status;
  const meta = STATUS_STYLE[key] || STATUS_STYLE.inactive;
  const Icon = meta.icon;
  return (
    <span data-testid={`status-badge-${key}`} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${meta.cls}`}>
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
}

function SummaryCard({ label, value, tone, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white px-4 py-3">
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <div className={`font-display text-2xl font-bold ${tone || "text-slate-900"}`}>{value}</div>
    </div>
  );
}

export default function AdminSubscriptions() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");    // all | trial_expired | trialing | active | past_due | canceled | inactive
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [runningBulk, setRunningBulk] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const [lookupEmail, setLookupEmail] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);

  const runLookup = async () => {
    const email = lookupEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) {
      toast.error("Paste an email address");
      return;
    }
    setLookupLoading(true);
    setLookupResult(null);
    try {
      const { data } = await api.get(`/admin/stripe/customer-lookup?email=${encodeURIComponent(email)}`);
      setLookupResult(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data) || "Lookup failed");
    } finally {
      setLookupLoading(false);
    }
  };

  const load = () => {
    setLoading(true);
    api.get("/admin/subscriptions")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const sendRenewal = async (company) => {
    if (!window.confirm(`Send renewal email to ${company.owner_email} for ${company.business_name}?\nThis will also flip trialing → past_due.`)) return;
    setBusyId(company.id);
    try {
      const { data: r } = await api.post(`/admin/subscriptions/${company.id}/send-renewal-link`, { mark_past_due: true });
      const status = r.email?.status;
      if (status === "sent") toast.success(`Renewal link emailed to ${company.owner_email}`);
      else toast.warning(`Checkout created but email status = ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data) || "Send failed");
    } finally {
      setBusyId(null);
    }
  };

  const reconcile = async (company) => {
    if (!window.confirm(`Reconcile ${company.business_name} from Stripe? This pulls the latest subscription state and unsticks accounts whose webhook was dropped.`)) return;
    setBusyId(company.id);
    try {
      const { data } = await api.post(`/admin/subscriptions/${company.id}/reconcile`, {});
      toast.success(`Reconciled — status now ${data.subscription?.["subscription.status"] || "updated"}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data) || "Reconcile failed");
    } finally { setBusyId(null); }
  };

  const cancel = async (company) => {
    const reason = window.prompt(`Cancel subscription for ${company.business_name}?\nOptional reason:`);
    if (reason === null) return;
    setBusyId(company.id);
    try {
      await api.post(`/admin/subscriptions/${company.id}/cancel`, { reason });
      toast.success("Subscription canceled");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data) || "Cancel failed");
    } finally {
      setBusyId(null);
    }
  };

  const runBulkExpire = async () => {
    const expiredCount = data?.summary?.trial_expired || 0;
    if (!window.confirm(`Email renewal link to all ${expiredCount} legacy expired trials?\n\nEach company will get a $97 Stripe checkout link that creates a REAL recurring subscription + card on file (not another one-time $1 trial). They will also be flipped to past_due in WSQ until they pay.\n\nIMPORTANT: If any of these companies have an ACTIVE Stripe sub under a different email (i.e. duplicates from the migration retry-storm), use the Duplicate-subscription cleanup widget first — otherwise you'll create a 2nd competing sub.`)) return;
    setRunningBulk(true);
    setBulkResult(null);
    try {
      const { data: r } = await api.post("/admin/subscriptions/process-expirations");
      setBulkResult(r);
      toast.success(`${r.processed_count} companies emailed · ${r.skipped_count} skipped`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data) || "Bulk run failed");
    } finally {
      setRunningBulk(false);
    }
  };

  const filtered = (data?.companies || []).filter((c) => {
    if (filter !== "all") {
      const key = c.trial_expired ? "trial_expired" : c.status;
      if (key !== filter) return false;
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const hay = `${c.business_name || ""} ${c.owner_email || ""} ${c.slug || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const s = data?.summary || {};

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button data-testid="admin-subs-back-btn" variant="outline" size="sm" onClick={() => nav("/admin")} className="rounded-xl">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Admin
          </Button>
          <div>
            <div className="font-display font-bold text-slate-900">Company Subscriptions</div>
            <div className="text-xs text-slate-500">See who&apos;s paying, who&apos;s stuck, and email renewal links in one click.</div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-5">
        {/* Bulk-run result — persists outside the banner so admin still sees it after the banner disappears */}
        {bulkResult && (
          <div data-testid="bulk-result-panel" className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-bold text-emerald-900">
                Processed {bulkResult.processed_count} · Skipped {bulkResult.skipped_count}
              </div>
              {bulkResult.processed?.length > 0 && (
                <div className="text-xs text-emerald-800/80 mt-1">
                  Emailed renewal link to: {bulkResult.processed.map((p) => p.business_name).join(", ")}
                </div>
              )}
              {bulkResult.skipped?.length > 0 && (
                <ul className="mt-1.5 space-y-0.5 text-[11px] text-emerald-900/80">
                  {bulkResult.skipped.map((sk) => (
                    <li key={sk.company_id}>• {sk.business_name}: {sk.reason}</li>
                  ))}
                </ul>
              )}
            </div>
            <button
              data-testid="dismiss-bulk-result"
              onClick={() => setBulkResult(null)}
              className="text-emerald-700 hover:text-emerald-900 text-xs font-semibold"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* ─── Duplicate-subscription cleanup tool ───────────────────────
             Takes a customer email → calls GET /admin/stripe/customer-lookup
             and labels each Stripe subscription KEEP / SAFE_TO_CANCEL / REVIEW
             so admin can nuke duplicates without breaking the owner's funnel.
          */}
        <div data-testid="dupe-sub-lookup" className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-slate-500" />
            <div className="font-display font-bold text-sm text-slate-900">Duplicate-subscription cleanup</div>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed mb-3">
            Paste a customer's email. You'll see every Stripe sub + which one WSQ is actually bound to.
            Cancel only the <span className="font-bold text-emerald-700">SAFE_TO_CANCEL</span> rows — never the <span className="font-bold text-amber-700">KEEP</span> one (that breaks their funnel).
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              value={lookupEmail}
              onChange={(e) => setLookupEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") runLookup(); }}
              placeholder="customer@email.com"
              data-testid="dupe-sub-email-input"
              className="h-10 rounded-xl max-w-sm"
            />
            <Button
              onClick={runLookup}
              disabled={lookupLoading}
              data-testid="dupe-sub-lookup-btn"
              size="sm"
              className="h-10 rounded-xl font-bold bg-slate-900 text-white hover:bg-slate-800"
            >
              {lookupLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-1.5" /> Look up</>}
            </Button>
          </div>

          {lookupResult && (
            <div className="mt-4 space-y-3" data-testid="dupe-sub-result">
              <div className="flex items-center gap-3 flex-wrap text-xs">
                <span className="px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 font-bold">KEEP: {lookupResult.summary.keep}</span>
                <span className="px-2 py-1 rounded-full bg-rose-100 text-rose-800 font-bold">SAFE TO CANCEL: {lookupResult.summary.safe_to_cancel}</span>
                {lookupResult.summary.review > 0 && (
                  <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-800 font-bold">REVIEW: {lookupResult.summary.review}</span>
                )}
                <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-600 font-semibold">Archived: {lookupResult.summary.archived}</span>
                <span className="text-slate-500">· {lookupResult.stripe_customer_count} Stripe customer{lookupResult.stripe_customer_count === 1 ? "" : "s"} · {lookupResult.wsq_company_count} WSQ company linkage{lookupResult.wsq_company_count === 1 ? "" : "s"}</span>
              </div>

              {lookupResult.wsq_companies.length > 0 && (
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs">
                  <div className="font-bold text-slate-800 mb-1.5">WSQ companies owned by this email:</div>
                  <ul className="space-y-1 font-mono text-[11px]">
                    {lookupResult.wsq_companies.map((c) => (
                      <li key={c.id} className="flex items-center gap-2 flex-wrap">
                        <span className="font-sans font-bold">{c.business_name}</span>
                        <span className="text-slate-500">({c.slug})</span>
                        <span className="text-slate-500">· status: {c.subscription_status || "—"}</span>
                        <span className="text-slate-500">· bound sub: {c.bound_subscription_id ? <code className="bg-white px-1 py-0.5 rounded">{c.bound_subscription_id.slice(0, 18)}…</code> : <em className="text-rose-600">none</em>}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {lookupResult.customers.map((cust) => (
                <div key={cust.stripe_customer_id} className="rounded-lg border border-slate-200 overflow-hidden">
                  <div className="bg-slate-50 border-b border-slate-200 px-3 py-2 flex items-center justify-between flex-wrap gap-2">
                    <div className="text-xs">
                      <span className="font-bold text-slate-800">{cust.email || "(no email)"}</span>
                      <span className="text-slate-500"> · </span>
                      <code className="text-[10px] text-slate-500">{cust.stripe_customer_id}</code>
                    </div>
                    <a
                      href={`https://dashboard.stripe.com/customers/${cust.stripe_customer_id}`}
                      target="_blank" rel="noreferrer"
                      className="text-[11px] text-[#C75D33] hover:underline font-semibold"
                    >
                      Open in Stripe ↗
                    </a>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {cust.subscriptions.length === 0 && (
                      <div className="px-3 py-2 text-xs text-slate-500 italic">No subscriptions</div>
                    )}
                    {cust.subscriptions.map((s) => (
                      <div key={s.subscription_id} className="px-3 py-2.5 flex items-start gap-3 text-xs" data-testid={`dupe-sub-row-${s.subscription_id}`}>
                        <span className={`shrink-0 mt-0.5 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wide ${
                          s.recommendation === "KEEP" ? "bg-emerald-100 text-emerald-800"
                          : s.recommendation === "SAFE_TO_CANCEL" ? "bg-rose-100 text-rose-800"
                          : s.recommendation === "REVIEW" ? "bg-amber-100 text-amber-800"
                          : "bg-slate-100 text-slate-500"
                        }`}>
                          {s.recommendation === "SAFE_TO_CANCEL" ? "cancel" : s.recommendation.toLowerCase()}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap font-mono text-[11px]">
                            <code className="bg-slate-50 px-1.5 py-0.5 rounded font-semibold">{s.subscription_id}</code>
                            <span className="text-slate-500 font-sans">· status: <span className="font-semibold text-slate-800">{s.status}</span></span>
                            {s.cancel_at_period_end && <span className="text-amber-700 font-sans text-[10px]">· cancels at period end</span>}
                            <a href={`https://dashboard.stripe.com/subscriptions/${s.subscription_id}`} target="_blank" rel="noreferrer" className="text-[10px] text-[#C75D33] hover:underline">↗</a>
                          </div>
                          <div className="text-slate-600 mt-0.5 leading-snug">{s.reason}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {lookupResult.note && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                  {lookupResult.note}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Alert banner when there are expired trials */}
        {s.trial_expired > 0 && (
          <div data-testid="expired-trials-banner" className="rounded-xl border border-rose-200 bg-rose-50 p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-bold text-rose-900">
                {s.trial_expired} {s.trial_expired === 1 ? "company has" : "companies have"} an expired trial that hasn&apos;t been auto-renewed by Stripe yet.
              </div>
              <div className="text-xs text-rose-800/80 mt-1 leading-relaxed">
                These are usually <strong>legacy customers</strong> from before the recurring-subs migration — their old $1 trial expired but their Stripe account was never set up with a real subscription + card on file. New customers auto-renew on Stripe; these legacy accounts need a one-time nudge.
                Click below to email each a $97 checkout link that creates a real recurring subscription (and flip them to past_due in the meantime).
                <strong className="block mt-1 text-rose-900">⚠️ Before clicking: use the Duplicate-subscription cleanup above with each affected email first</strong> — if they already have an active Stripe sub, don&apos;t re-send; just reconcile.
              </div>
              <Button
                data-testid="run-bulk-expire-btn"
                onClick={runBulkExpire}
                disabled={runningBulk}
                className="mt-3 rounded-xl bg-rose-600 hover:bg-rose-700 text-white h-9 px-4 inline-flex items-center gap-1.5"
              >
                {runningBulk ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                Email renewal link to {s.trial_expired} legacy expired trial{s.trial_expired === 1 ? "" : "s"}
              </Button>
            </div>
          </div>
        )}

        {/* Summary grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <SummaryCard label="Total" value={s.total ?? "–"} testid="sum-total" />
          <SummaryCard label="Trial expired" value={s.trial_expired ?? "–"} tone="text-rose-600" testid="sum-expired" />
          <SummaryCard label="Trialing" value={s.trialing ?? "–"} tone="text-amber-600" testid="sum-trialing" />
          <SummaryCard label="Active" value={s.active ?? "–"} tone="text-emerald-600" testid="sum-active" />
          <SummaryCard label="Past due" value={s.past_due ?? "–"} tone="text-rose-600" testid="sum-past-due" />
          <SummaryCard label="Canceled" value={s.canceled ?? "–"} tone="text-slate-500" testid="sum-canceled" />
        </div>

        {/* Filters */}
        <div className="rounded-xl border border-slate-200 bg-white p-3 flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap gap-1.5">
            {[
              ["all", "All"],
              ["trial_expired", "Expired trials"],
              ["trialing", "Trialing"],
              ["active", "Active"],
              ["past_due", "Past due"],
              ["canceled", "Canceled"],
              ["inactive", "Inactive"],
            ].map(([k, label]) => (
              <button
                key={k}
                data-testid={`filter-${k}`}
                onClick={() => setFilter(k)}
                className={`text-xs h-8 px-3 rounded-full border font-medium ${filter === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex-1 min-w-[200px] relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="subs-search"
              placeholder="Search name, email, slug…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 rounded-lg pl-9"
            />
          </div>
        </div>

        {/* Table */}
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          {loading ? (
            <div className="px-6 py-10 text-center text-slate-500 text-sm">
              <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-6 py-10 text-center text-slate-500 text-sm">No companies match your filter.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] font-bold uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2.5">Company</th>
                  <th className="text-left px-4 py-2.5">Status</th>
                  <th className="text-left px-4 py-2.5">Trial ends / Ended</th>
                  <th className="text-left px-4 py-2.5">Last payment</th>
                  <th className="text-right px-4 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} data-testid={`sub-row-${c.id}`} className="border-t border-slate-100 hover:bg-slate-50/70">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900 truncate max-w-[240px]" title={c.business_name}>{c.business_name || "—"}</div>
                      <div className="text-[11px] text-slate-500 truncate max-w-[240px]">
                        {c.owner_email || "no owner email"} · <span className="text-slate-400">/{c.slug}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col items-start gap-1">
                        <StatusBadge status={c.status} trialExpired={c.trial_expired} />
                        {c.auto_renews && (
                          <span data-testid={`auto-renews-${c.id}`} className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                            Auto-renews
                          </span>
                        )}
                        {c.cancel_at_period_end && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                            Cancels at period end
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-700">
                      {c.trial_end ? (
                        <>
                          <div>{new Date(c.trial_end).toLocaleDateString()}</div>
                          {c.days_since_trial_end != null && (
                            <div className={c.trial_expired ? "text-rose-600 font-semibold" : "text-slate-500"}>
                              {c.days_since_trial_end}d ago
                            </div>
                          )}
                          {c.days_to_trial_end != null && (
                            <div className="text-slate-500">in {c.days_to_trial_end}d</div>
                          )}
                        </>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-700">
                      {c.last_payment_at ? new Date(c.last_payment_at).toLocaleDateString() : <span className="text-slate-400">never</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1.5">
                        <Button
                          data-testid={`send-renewal-${c.id}`}
                          size="sm"
                          onClick={() => sendRenewal(c)}
                          disabled={busyId === c.id || !c.owner_email}
                          title={!c.owner_email ? "No owner email on file" : "Email a $97 renewal checkout link"}
                          className="rounded-lg bg-[#C75D33] hover:bg-[#A84C28] text-white h-8 px-2.5 text-xs inline-flex items-center gap-1"
                        >
                          {busyId === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                          Send renewal
                        </Button>
                        <Button
                          data-testid={`reconcile-${c.id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => reconcile(c)}
                          disabled={busyId === c.id}
                          title="Pull latest state from Stripe (unsticks accounts where the webhook was dropped)"
                          className="rounded-lg h-8 px-2.5 text-xs"
                        >
                          Reconcile
                        </Button>
                        {["trialing", "active", "past_due", "paused"].includes(c.status) && (
                          <Button
                            data-testid={`cancel-sub-${c.id}`}
                            size="sm"
                            variant="outline"
                            onClick={() => cancel(c)}
                            disabled={busyId === c.id}
                            className="rounded-lg h-8 px-2.5 text-xs text-rose-700 border-rose-200 hover:bg-rose-50"
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="text-[11px] text-slate-400 leading-relaxed">
          ℹ️ Billing model note: WindowScreenQuote uses <strong>true Stripe Subscriptions</strong> (recurring, with card-on-file auto-renew). The old one-time Checkout model was retired on 2026-08-09.
          For companies still on the legacy flow (migrated accounts with no card attached), use the &quot;Send renewal&quot; action to email a checkout link that creates a real recurring sub.
          Any company that shows mismatched state vs. Stripe can be fixed with the &quot;Reconcile&quot; button or the Duplicate-subscription cleanup above.
        </div>
      </main>
    </div>
  );
}
